import { IRole, UserRoles } from "@/types/user.type";

const ROLE_IDS_BY_CODE = {
  SAD: 1,
  PAD: 2,
  PUS: 3,
  PLE: 4,
  PST: 5,
  CAD: 10,
  CUS: 11,
  CLE: 12,
  CST: 13,
  PBDE: 5,
  CBDE: 13
} as const;

/** Converts a Portal role code to the numeric ID required by the FIDO API. */
export const toRoleId = (
  roleId: string | number | undefined
): number | undefined => {
  if (typeof roleId === "number") return roleId;

  return roleId === undefined
    ? undefined
    : ROLE_IDS_BY_CODE[roleId as keyof typeof ROLE_IDS_BY_CODE];
};

export const UserRole = {
  SAD: "USER_ROLE.SUPER_ADMIN",
  PAD: "USER_ROLE.PARTNER_ADMIN",
  PUS: "USER_ROLE.PARTNER_USER",
  PLE: "USER_ROLE.PARTNER_LEAK_ENGINEER",
  PBDE: "USER_ROLE.PARTNER_BUG_DEPLOYMENT_ENGINEER",
  CAD: "USER_ROLE.COMPANY_ADMIN",
  CUS: "USER_ROLE.COMPANY_USER",
  CLE: "USER_ROLE.COMPANY_LEAK_ENGINEER",
  CBDE: "USER_ROLE.COMPANY_BUG_DEPLOYMENT_ENGINEER"
};

export const checkRestictedPermissions = (permissions: string[], role = "") =>
  permissions.includes(role);

export const filterRolesAccordingToUserRole = (
  roles: IRole[],
  code: string
) => {
  const adminRoles = [UserRoles.COMPANY_ADMIN, UserRoles.SUPER_ADMIN];

  switch (code) {
    case UserRoles.SUPER_ADMIN:
      return roles;
    case UserRoles.COMPANY_ADMIN:
      return roles.filter((role) => role.role_code !== UserRoles.SUPER_ADMIN);
    default:
      return roles.filter((role) => !adminRoles.includes(role.role_code));
  }
};

export const isSuperUser = (role_id?: number | null): boolean => {
  return role_id === 1;
};

export const isPartnerUser = (role_id?: number | null): boolean => {
  return role_id === 2 || role_id === 3 || role_id === 4 || role_id === 5;
};

export const isCompanyUser = (role_id?: number | null): boolean => {
  return role_id === 10 || role_id === 11 || role_id === 12 || role_id === 13;
};

export const partnerRoleIds = [2, 3, 4, 5];
export const companyRoleIds = [10, 11, 12, 13, 14];

export const partnerAdminId = 2;
export const companyAdminId = 10;

export const SUPER_ADMIN_CODE = UserRoles.SUPER_ADMIN;
export const PARTNER_ADMIN_CODE = UserRoles.PARTNER_ADMIN;
export const COMPANY_ADMIN_CODE = UserRoles.COMPANY_ADMIN;

export const partnerRoleCodes: string[] = [
  UserRoles.PARTNER_ADMIN,
  UserRoles.PARTNER_USER,
  UserRoles.PARTNER_LEAK_ENGINEER,
  UserRoles.PARTNER_BUG_DEPLOYMENT_ENGINEER
];

export const companyRoleCodes: string[] = [
  UserRoles.COMPANY_ADMIN,
  UserRoles.COMPANY_USER,
  UserRoles.COMPANY_LEAK_ENGINEER,
  UserRoles.COMPANY_BUG_DEPLOYMENT_ENGINEER
];

export const isSuperUserByCode = (roleId?: string | number | null): boolean => {
  if (typeof roleId === "string") return roleId === SUPER_ADMIN_CODE;
  return roleId === 1;
};

export const isPartnerByCode = (roleId?: string | number | null): boolean => {
  if (typeof roleId === "string") return partnerRoleCodes.includes(roleId);
  return partnerRoleIds.includes(roleId as number);
};

export const isCompanyByCode = (roleId?: string | number | null): boolean => {
  if (typeof roleId === "string") return companyRoleCodes.includes(roleId);
  return companyRoleIds.includes(roleId as number);
};
